import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Server, Check, LayoutDashboard, ShoppingCart, Loader2, Search, Activity, HeadphonesIcon, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import DOMPurify from 'dompurify';import StorefrontSection from '@/components/StorefrontSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WL_INTENT_KEY = 'cloudsetu_wl_order_intent';

const BILLING_CYCLES = [
  { value: 'monthly',     label: 'Monthly',     months: 1,  days: 30   },
  { value: 'quarterly',   label: 'Quarterly',   months: 3,  days: 90   },
  { value: 'half_yearly', label: 'Half Yearly', months: 6,  days: 180  },
  { value: 'yearly',      label: 'Yearly',      months: 12, days: 365  },
  { value: 'two_years',   label: 'Two Years',   months: 24, days: 730  },
  { value: 'three_years', label: 'Three Years', months: 36, days: 1095 },
];

function PublishedBuilderSections({ sections, settings, products, onGetStarted, onOrderNow }) {
  const visibleSections = sections.filter(section => section.visible !== false);
  return (
    <main>
      {visibleSections.map((section, sectionIndex) => {
        const cards = Array.isArray(section.cards) ? section.cards : [];
        const variant = String(section.variant || '').toLowerCase();
        const gridClass = section.type === 'trust' && variant.includes('logo strip')
          ? 'flex flex-wrap justify-center'
          : section.type === 'blog' && variant.includes('horizontal')
            ? 'grid-cols-1'
            : variant.includes('2-column') || variant.includes('two column')
              ? 'md:grid-cols-2'
              : 'md:grid-cols-3';
        const heading = <h2 className="text-3xl font-bold tracking-tight" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(section.heading || '') }} />;
        const description = section.subtext && <div className="rich-text-content mx-auto mt-3 max-w-3xl text-slate-600" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(section.subtext || '') }} />;

        // Skip the builder's header section — the platform navbar (above) already
        // provides branding-aware header with logo, login/signup buttons.
        if (section.type === 'header') return null;

        if (section.type === 'hero') {
          const dark = variant.includes('dark gradient');
          const alignment = variant.includes('modern split') ? 'text-left' : 'text-center';
          return (
            <section key={section.id || sectionIndex} className={`px-6 py-20 text-white ${alignment}`} style={dark ? { background: 'linear-gradient(120deg,#0f172a,#1d4ed8)' } : { backgroundColor: settings.primary_color }}>
              <div className="mx-auto max-w-4xl">
                <h1 className="text-4xl font-bold tracking-tight sm:text-6xl" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(section.heading || '') }} />
                <div className={`rich-text-content mt-5 max-w-3xl text-lg text-white/85 ${alignment === 'text-center' ? 'mx-auto' : ''}`} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(section.subtext || '') }} />
                <Button className="mt-8 border-0 px-8 text-white" style={{ backgroundColor: settings.accent_color }} onClick={onGetStarted}>
                  {section.primaryText || 'Get Started'}
                </Button>
              </div>
            </section>
          );
        }

        if (section.type === 'footer') {
          return (
            <footer key={section.id || sectionIndex} className="bg-slate-950 px-6 py-12 text-white">
              <div className="mx-auto grid max-w-7xl gap-8 md:grid-cols-4">
                <div>
                  {settings.logo_url && (
                    <img src={settings.logo_url} alt={settings.company_name || 'Logo'} className="mb-3 max-h-10 w-auto object-contain brightness-0 invert" />
                  )}
                  <h2 className="text-lg font-bold" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(section.heading || '') }} />
                  <div className="rich-text-content mt-2 text-sm text-slate-300" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(section.subtext || '') }} />
                </div>
                {cards.map((card, index) => <div key={`${card.title}-${index}`}><h3 className="font-semibold">{card.title}</h3><p className="mt-2 whitespace-pre-line text-sm text-slate-300">{card.text}</p></div>)}
              </div>
              <p className="mx-auto mt-8 max-w-7xl border-t border-white/10 pt-5 text-xs text-slate-400">© {new Date().getFullYear()} {settings.company_name}. All rights reserved.</p>
            </footer>
          );
        }

        // products section: auto-fetches /api/products, passes order callback
        if (section.type === 'products') {
          return <StorefrontSection key={section.id || sectionIndex} section={section} onOrder={onOrderNow} />;
        }

        return (
          <section key={section.id || sectionIndex} id={section.type === 'pricing' ? 'plans-section' : undefined} className={`px-5 py-16 ${
            section.type === 'pricing' ? 'bg-slate-50'
              : section.type === 'cta' && variant.includes('gradient') ? 'bg-gradient-to-r from-blue-50 to-violet-50'
                : section.type === 'cta' ? 'bg-blue-50'
                  : 'bg-white'
          }`}>
            <div className="mx-auto max-w-7xl">
              <div className="mb-10 text-center">{heading}{description}</div>
              {section.type === 'cta' && (
                <div className="text-center">
                  <Button className="border-0 px-8 text-white" style={{ backgroundColor: settings.accent_color }} onClick={onGetStarted}>{section.primaryText || 'Get Started'}</Button>
                </div>
              )}
              {section.type === 'pricing' && (
                <div className={`grid gap-6 ${gridClass}`}>
                  {cards.map((card, index) => {
                    const product = products[index];
                    return (
                      <Card key={`${card.title}-${index}`} className={card.featured ? 'border-blue-500 shadow-lg ring-1 ring-blue-500' : ''}>
                        <CardHeader><p className="text-sm text-slate-500">{card.title}</p><CardTitle className="text-3xl" style={{ color: settings.accent_color }}>{card.price}<span className="text-sm font-normal text-slate-500">{card.period}</span></CardTitle></CardHeader>
                        <CardContent className="space-y-4">
                          <p className="text-sm text-slate-600">{card.text}</p>
                          {Array.isArray(card.list) && <ul className="space-y-2 text-sm text-slate-600">{card.list.map((item, itemIndex) => <li key={`${item}-${itemIndex}`}>✓ {item}</li>)}</ul>}
                          <Button className="w-full border-0 text-white" style={{ backgroundColor: settings.accent_color }} onClick={() => product ? onOrderNow(product) : onGetStarted()}>
                            {product ? 'Order Now' : (section.primaryText || 'Get Started')}
                          </Button>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
              {section.type === 'features' && (
                <div className={`grid gap-6 ${gridClass}`}>
                  {cards.map((card, index) => <Card key={`${card.title}-${index}`} className="border-slate-200"><CardContent className="p-6"><span className="text-3xl">{card.icon}</span><h3 className="mt-3 text-lg font-semibold">{card.title}</h3><p className="mt-2 text-sm text-slate-600">{card.text}</p>{Array.isArray(card.list) && <ul className="mt-4 space-y-2 text-sm text-slate-500">{card.list.map((item, itemIndex) => <li key={`${item}-${itemIndex}`}>✓ {item}</li>)}</ul>}</CardContent></Card>)}
                </div>
              )}
              {['testimonials', 'trust', 'blog', 'slider'].includes(section.type) && (
                <div className={`${section.type === 'trust' && variant.includes('logo strip') ? 'flex flex-wrap justify-center gap-4' : `grid gap-6 ${gridClass}`}`}>
                  {cards.map((card, index) => <Card key={`${card.title}-${index}`} className={`${section.type === 'testimonials' && variant.includes('spotlight') && index === 0 ? 'md:col-span-2' : ''} ${section.type === 'trust' && variant.includes('logo strip') ? 'min-w-44' : ''}`}><CardContent className="p-6"><span className="text-2xl">{card.icon}</span><h3 className="mt-3 font-semibold">{card.title || card.category}</h3><p className="mt-2 text-sm text-slate-600">{card.text || card.summary || card.date}</p>{card.author && <p className="mt-3 text-xs font-medium text-slate-500">{card.author} · {card.role}</p>}{section.type === 'slider' && <Button className="mt-4 border-0 text-white" style={{ backgroundColor: settings.accent_color }} onClick={onGetStarted}>{card.buttonText || section.primaryText}</Button>}</CardContent></Card>)}
                </div>
              )}
              {section.type === 'faq' && (
                <div className="mx-auto max-w-3xl space-y-3">
                  {cards.map((card, index) => <details key={`${card.question}-${index}`} className="rounded-xl border border-slate-200 bg-white p-5"><summary className="cursor-pointer font-semibold">{card.question}</summary><p className="mt-3 text-sm text-slate-600">{card.answer}</p></details>)}
                </div>
              )}
            </div>
          </section>
        );
      })}
    </main>
  );
}

export default function PublicLanding() {
  const { subdomain } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // ── All useState hooks at the TOP — before any return ──
  const [data, setData]                       = useState(null);
  const [loading, setLoading]                 = useState(true);
  const [billingModal, setBillingModal]       = useState({ open: false, product: null });
  const [selectedCycle, setSelectedCycle]     = useState('monthly');
  const [orderSubmitting, setOrderSubmitting] = useState(false);
  const [orderConfirmed, setOrderConfirmed]   = useState(false);
  const [activeCategory, setActiveCategory]   = useState("All");
  const [searchQuery, setSearchQuery]         = useState("");

  const intentHandledRef = useRef(false);

  useEffect(() => { fetchData(); }, [subdomain]);

  // After data + user both loaded: detect sessionStorage intent for auto-open
  useEffect(() => {
    if (!user || !data || intentHandledRef.current) return;
    if (user.role !== 'client') return;
    const raw = sessionStorage.getItem(WL_INTENT_KEY);
    if (!raw) return;
    try {
      const intent = JSON.parse(raw);
      if (intent.subdomain !== subdomain) return;
      intentHandledRef.current = true;
      const match = data.products.find(p => p.id === intent.productId);
      if (!match) {
        toast.info(`Redirected back for "${intent.productName}" — please select it below.`);
        return;
      }
      openBillingModal(match);
    } catch { /* ignore parse errors */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, data]);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/white-label/public/${subdomain}`);
      setData(res.data);
    } catch {
      toast.error('Subdomain not found');
    } finally {
      setLoading(false);
    }
  };

  const openBillingModal = (product) => {
    setSelectedCycle('monthly');
    setOrderConfirmed(false);
    setBillingModal({ open: true, product });
  };

  const handleOrderNow = (product) => {
    if (!user) {
      const { settings } = data;
      const partnerAccountId = data.partner_account_id || settings.partner_account_id || '';
      const intent = {
        productId:            product.id,
        productName:          product.name || product.product_name || '',
        productCategory:      product.category || '',
        billingPricesDisplay: product.billing_prices_display || {},
        subdomain,
        whiteLabelUrl: `/public/${subdomain}`,
      };
      sessionStorage.setItem(WL_INTENT_KEY, JSON.stringify(intent));
      navigate(`/client/signup${partnerAccountId ? `?ref=${partnerAccountId}` : ''}`);
      return;
    }
    if (user.role === 'client') {
      openBillingModal(product);
      return;
    }
    const dash = user.role === 'admin' || user.role === 'support_admin'
      ? '/admin/dashboard'
      : '/reseller/dashboard';
    navigate(dash);
  };

  const handlePlaceOrder = async () => {
    if (!billingModal.product || !selectedCycle) return;
    const product  = billingModal.product;
    const prices   = product.billing_prices_display || {};
    const cycleInfo = BILLING_CYCLES.find(c => c.value === selectedCycle) || BILLING_CYCLES[0];
    const unitPrice = prices[selectedCycle] || product.display_price || 0;

    setOrderSubmitting(true);
    try {
      await axios.post(`${API}/client/order-requests`, {
        product_id:       product.id,
        product_name:     product.name || product.product_name || '',
        product_category: product.category || 'Hosting',
        billing_cycle:    selectedCycle,
        unit_price:       unitPrice,
        quantity:         1,
        validity_days:    cycleInfo.days,
        source:           'white_label',
      });
      sessionStorage.removeItem(WL_INTENT_KEY);
      intentHandledRef.current = true;
      setOrderConfirmed(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to place order. Please try again.');
    } finally {
      setOrderSubmitting(false);
    }
  };

  // ── Early returns (hooks are all above, so this is safe) ──
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-slate-800 mb-2">Subdomain Not Found</h1>
          <p className="text-slate-500">The subdomain you&apos;re looking for doesn&apos;t exist</p>
        </div>
      </div>
    );
  }

  // ── Data is available here ──
  const { settings, products } = data;
  const builderState = data.page_builder || settings.page_builder || data.published_storefront;
  const activePublishedPage = builderState?.pages?.[builderState.activeTemplatePage];
  const publicSnapshot = data.published_page || data.published_storefront;
  const publicSnapshotIsPublished = Boolean(publicSnapshot && publicSnapshot.published !== false);
  const publishedPage = (publicSnapshotIsPublished ? publicSnapshot : null) || activePublishedPage || builderState;
  const hasPublishedBuilderPage = Array.isArray(publishedPage?.sections) && (
    publicSnapshotIsPublished ||
    activePublishedPage?.published === true ||
    builderState?.published === true
  );
  const publishedSections = hasPublishedBuilderPage ? publishedPage.sections : null;
  const partnerAccountId = data.partner_account_id || settings.partner_account_id || '';
  const partnerSignupUrl = `/client/signup${partnerAccountId ? `?ref=${partnerAccountId}` : ''}`;

  const handleGetStarted = () => {
    if (user) {
      const dash = user.role === 'client' ? '/client/dashboard'
        : user.role === 'admin' || user.role === 'support_admin' ? '/admin/dashboard'
        : '/reseller/dashboard';
      navigate(dash);
    } else {
      navigate(partnerSignupUrl);
    }
  };

  const scrollToPlans = () =>
    document.getElementById('plans-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const getPriceForCycle = (product, cycle) => {
    if (!product) return null;
    const prices = product.billing_prices_display || {};
    return prices[cycle] || product.display_price || null;
  };

  // ── Filtered products (derived value, not a hook — safe after early returns) ──
  const filteredProducts = products.filter((p) => {
    const matchCat = activeCategory === "All" || p.category === activeCategory;
    const matchSearch =
      !searchQuery ||
      p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.category?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchCat && matchSearch;
  });

  const categories = ["All", ...new Set(products.map((p) => p.category).filter(Boolean))];

  return (
    <div className="min-h-screen">

      {/* ── Navbar ── */}
      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-2">
              {settings.logo_url ? (
                <img
                  src={settings.logo_url}
                  alt={settings.company_name}
                  className="max-h-12 w-auto object-contain"
                />
              ) : (
                <Server className="h-6 w-6" style={{ color: settings.accent_color }} />
              )}
            </div>

            {user ? (
              <Button
                className="text-white border-0 flex items-center gap-2"
                style={{ backgroundColor: settings.accent_color }}
                onClick={handleGetStarted}
                data-testid="dashboard-button"
              >
                <LayoutDashboard className="h-4 w-4" /> Go to Dashboard
              </Button>
            ) : (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  className="border text-slate-700 bg-white"
                  style={{ borderColor: settings.accent_color, color: settings.accent_color }}
                  onClick={() => navigate(`/client/login?next=${encodeURIComponent(`/public/${subdomain}`)}`)}
                  data-testid="login-button"
                >
                  Login
                </Button>
                <Button
                  className="text-white border-0"
                  style={{ backgroundColor: settings.accent_color }}
                  onClick={() => navigate(partnerSignupUrl)}
                  data-testid="signup-button"
                >
                  Sign Up
                </Button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {publishedSections ? (
        <PublishedBuilderSections
          sections={publishedSections}
          settings={settings}
          products={products}
          onGetStarted={handleGetStarted}
          onOrderNow={handleOrderNow}
        />
      ) : <>
      {/* ── Hero ── */}
      <section className="text-white py-20" style={{ backgroundColor: settings.primary_color }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl lg:text-6xl font-bold tracking-tight mb-6" data-testid="hero-title">
              Premium Hosting Solutions
            </h1>
            <p className="text-xl lg:text-2xl text-white/90 mb-8">
              Powered by {settings.company_name} – Your trusted hosting partner
            </p>
            <Button
              size="lg"
              className="text-white text-lg px-8 py-6 border-0"
              style={{ backgroundColor: settings.accent_color }}
              onClick={scrollToPlans}
              data-testid="hero-cta-button"
            >
              View Our Plans
            </Button>
          </div>
        </div>
      </section>

      {/* ── Products ── */}
      <section id="plans-section" className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold tracking-tight mb-4" data-testid="products-section-title">
              Our Hosting Plans
            </h2>
            <p className="text-lg text-slate-600">Choose the perfect plan for your needs</p>
          </div>

          {/* ── Category Tabs + Search ── */}
          <div className="flex flex-col sm:flex-row gap-4 mb-8 items-start sm:items-center justify-between">
            {/* Tabs */}
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors border ${
                    activeCategory === cat
                      ? "text-white border-transparent"
                      : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                  }`}
                  style={
                    activeCategory === cat
                      ? { backgroundColor: settings.accent_color, borderColor: settings.accent_color }
                      : {}
                  }
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search plans..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:border-transparent"
              />
            </div>
          </div>

          {/* ── Cards ── */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProducts.map((product) => (
              <Card
                key={product.id}
                className="hover:shadow-xl transition-shadow flex flex-col"
                data-testid={`product-card-${product.id}`}
              >
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm text-slate-500 uppercase tracking-wide">{product.category}</p>
                      <CardTitle className="text-2xl mt-2">{product.name}</CardTitle>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="flex flex-col flex-grow space-y-6">
                  <p className="text-slate-600">{product.description}</p>

                  {settings.show_pricing && product.display_price && (
                    <div className="border-t border-b py-4">
                      <div className="flex items-baseline">
                        <span
                          className="text-4xl font-bold"
                          style={{ color: settings.accent_color }}
                          data-testid={`product-price-${product.id}`}
                        >
                          ₹{product.display_price.toLocaleString()}
                        </span>
                        <span className="text-slate-500 ml-2">/month</span>
                      </div>
                    </div>
                  )}

                  {product.features && product.features.length > 0 && (
                    <ul className="space-y-3 flex-grow">
                      {product.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <Check
                            className="h-5 w-5 flex-shrink-0 mt-0.5"
                            style={{ color: settings.accent_color }}
                          />
                          <span className="text-sm text-slate-600">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Button always at bottom */}
                  <div className="mt-auto pt-2">
                    <Button
                      className="w-full text-white border-0"
                      style={{ backgroundColor: settings.accent_color }}
                      onClick={() => handleOrderNow(product)}
                      data-testid={`order-button-${product.id}`}
                    >
                      Order Now
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* No results */}
          {filteredProducts.length === 0 && (
            <div className="text-center py-16 text-slate-400">
              <p className="text-lg">
                No plans found{searchQuery ? ` for "${searchQuery}"` : ' in this category'}.
              </p>
            </div>
          )}
        </div>
      </section>

     {/* ── Features ── */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            {[
              {
                title: '99.9% Uptime',
                desc: 'Reliable infrastructure you can count on',
                icon: <Activity className="h-8 w-8" style={{ color: settings.accent_color }} />,
              },
              {
                title: '24/7 Support',
                desc: 'Expert support whenever you need it',
                icon: <HeadphonesIcon className="h-8 w-8" style={{ color: settings.accent_color }} />,
              },
              {
                title: 'Secure Hosting',
                desc: 'Advanced security measures included',
                icon: <ShieldCheck className="h-8 w-8" style={{ color: settings.accent_color }} />,
              },
            ].map(({ title, desc, icon }) => (
              <div key={title}>
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-50 mb-4">
                  {icon}
                </div>
                <h3 className="text-xl font-semibold mb-2">{title}</h3>
                <p className="text-slate-600">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="text-white py-12" style={{ backgroundColor: settings.primary_color }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {settings.logo_url && (
            <img src={settings.logo_url} alt={settings.company_name || 'Logo'} className="mx-auto mb-4 max-h-10 w-auto object-contain brightness-0 invert" />
          )}
          <p className="text-white/80">© {new Date().getFullYear()} {settings.company_name}. All rights reserved.</p>
          <p className="text-sm text-white/60 mt-2">Powered by CloudSetu Private Limited</p>
        </div>
      </footer>
      </>}

      {/* ── Billing Cycle Selector Modal ── */}
      <Dialog
        open={billingModal.open}
        onOpenChange={open => {
          if (!orderSubmitting) {
            setBillingModal(m => ({ ...m, open }));
            if (!open) setOrderConfirmed(false);
          }
        }}
      >
        <DialogContent className="sm:max-w-md" data-testid="billing-cycle-modal">
          {!orderConfirmed ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <ShoppingCart className="h-5 w-5" style={{ color: settings?.accent_color }} />
                  Select Billing Cycle
                </DialogTitle>
                <p className="text-sm text-slate-500 mt-1">
                  Plan: <strong>{billingModal.product?.name || billingModal.product?.product_name}</strong>
                </p>
              </DialogHeader>

              <div className="space-y-2 py-2">
                {BILLING_CYCLES.map((cycle) => {
                  const price      = getPriceForCycle(billingModal.product, cycle.value);
                  const isSelected = selectedCycle === cycle.value;
                  return (
                    <button
                      key={cycle.value}
                      onClick={() => setSelectedCycle(cycle.value)}
                      data-testid={`cycle-option-${cycle.value}`}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border-2 transition-all text-left ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                          isSelected ? 'border-blue-500' : 'border-slate-300'
                        }`}>
                          {isSelected && <div className="w-2 h-2 rounded-full bg-blue-500" />}
                        </div>
                        <div>
                          <p className={`font-medium text-sm ${isSelected ? 'text-blue-700' : 'text-slate-800'}`}>
                            {cycle.label}
                          </p>
                          <p className="text-xs text-slate-400">{cycle.months} month{cycle.months > 1 ? 's' : ''}</p>
                        </div>
                      </div>
                      {settings?.show_pricing && price ? (
                        <div className="text-right">
                          <p className={`font-semibold text-sm ${isSelected ? 'text-blue-700' : 'text-slate-700'}`}>
                            ₹{price.toLocaleString()}
                          </p>
                          <p className="text-xs text-slate-400">
                            ₹{Math.round(price / cycle.months).toLocaleString()}/mo
                          </p>
                        </div>
                      ) : null}
                    </button>
                  );
                })}
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setBillingModal(m => ({ ...m, open: false }))}
                  disabled={orderSubmitting}
                  data-testid="billing-modal-cancel"
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 text-white border-0"
                  style={{ backgroundColor: settings?.accent_color }}
                  onClick={handlePlaceOrder}
                  disabled={orderSubmitting || !selectedCycle}
                  data-testid="billing-modal-place-order"
                >
                  {orderSubmitting
                    ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Placing…</>
                    : 'Place Order'}
                </Button>
              </div>
            </>
          ) : (
            <div className="text-center py-6 space-y-4" data-testid="order-confirmation">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-50 mx-auto">
                <Check className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-800">Order Placed!</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Your order for <strong>{billingModal.product?.name}</strong> (
                {BILLING_CYCLES.find(c => c.value === selectedCycle)?.label}) has been submitted.
                Your partner will review and respond shortly.
              </p>
              <div className="flex flex-col gap-2 pt-2">
                <Button
                  className="w-full text-white border-0"
                  style={{ backgroundColor: settings?.accent_color }}
                  onClick={() => {
                    setBillingModal({ open: false, product: null });
                    setOrderConfirmed(false);
                    navigate('/client/dashboard');
                  }}
                  data-testid="order-confirmation-dashboard"
                >
                  Go to Dashboard
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setBillingModal({ open: false, product: null });
                    setOrderConfirmed(false);
                  }}
                  data-testid="order-confirmation-close"
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
}